/**
 * Feito pelo agente netexperts
 * Módulo: AnalyticsObra — análise mês a mês das vendas (VGV x recebido x a receber)
 */
import { formatCurrency, formatNumber, formatPercent } from '../../../../utils/obraAnalytics'

const BarraConversao = ({ vgv, recebido, aReceber }) => {
    if (!vgv) return <span className="text-xs text-gray-300">—</span>
    const pctRec = Math.min(100, Math.max(0, (recebido / vgv) * 100))
    const pctAre = Math.min(100 - pctRec, Math.max(0, (aReceber / vgv) * 100))
    return (
        <div className="flex h-2 rounded-full overflow-hidden bg-gray-100 min-w-[80px]">
            {pctRec > 0 && <div className="bg-emerald-500" style={{ width: `${pctRec}%` }} title={`Recebido ${formatPercent(pctRec)}`} />}
            {pctAre > 0 && <div className="bg-amber-400" style={{ width: `${pctAre}%` }} title={`A receber ${formatPercent(pctAre)}`} />}
        </div>
    )
}

const CenarioResumo = ({ cards, meses }) => {
    const mesesComVenda = meses.filter((m) => m.quantidade > 0)
    const isUnicoMes = mesesComVenda.length === 1
    const mes = isUnicoMes ? mesesComVenda[0] : null

    if (isUnicoMes && mes) {
        return (
            <div className="rounded-xl bg-blue-50 border border-blue-100 p-4 space-y-2">
                <p className="text-xs font-bold uppercase tracking-wide text-blue-700">Cenário do mês — {mes.label ?? mes.mes_ano}</p>
                <p className="text-sm text-blue-900 leading-relaxed">
                    Foram vendidos <b>{formatNumber(mes.quantidade)} contrato(s)</b> totalizando{' '}
                    <b>{formatCurrency(mes.vgv)}</b> de VGV. Deste montante,{' '}
                    <b className="text-emerald-700">{formatCurrency(mes.recebido)}</b> já foi recebido ({formatPercent(mes.pct_recebido)}) e{' '}
                    <b className="text-amber-700">{formatCurrency(mes.a_receber)}</b> permanece a receber.
                </p>
            </div>
        )
    }

    const mesMaiorVgv = [...mesesComVenda].sort((a, b) => b.vgv - a.vgv)[0]
    const mesMenorConversao = [...mesesComVenda].filter((m) => m.vgv > 0).sort((a, b) => a.pct_recebido - b.pct_recebido)[0]

    return (
        <div className="rounded-xl bg-blue-50 border border-blue-100 p-4 space-y-2">
            <p className="text-xs font-bold uppercase tracking-wide text-blue-700">Cenário do período</p>
            <p className="text-sm text-blue-900 leading-relaxed">
                No período, <b>{formatNumber(cards.num_vendas)} contrato(s)</b> geraram{' '}
                <b>{formatCurrency(cards.vgv)}</b> de VGV. Já recebido:{' '}
                <b className="text-emerald-700">{formatCurrency(cards.total_recebido)}</b> ({formatPercent(cards.pct_recebido)}).
                Saldo a receber: <b className="text-amber-700">{formatCurrency(cards.total_a_receber)}</b>.
            </p>
            {mesMaiorVgv && (
                <p className="text-xs text-blue-700">
                    Maior volume de vendas em <b>{mesMaiorVgv.label}</b> ({formatCurrency(mesMaiorVgv.vgv)}).
                    {mesMenorConversao && mesMenorConversao.pct_recebido < 100 && (
                        <> Menor conversão em <b>{mesMenorConversao.label}</b> ({formatPercent(mesMenorConversao.pct_recebido)} recebido).</>
                    )}
                </p>
            )}
        </div>
    )
}

const VendasMensalPanel = ({ serie = [], cards = {} }) => {
    const meses = serie
    const temVendas = meses.some((m) => m.quantidade > 0)

    return (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 space-y-1">
                <h3 className="text-base font-semibold text-gray-800">Análise mês a mês — realização financeira</h3>
                <p className="text-xs text-gray-500">
                    Por mês de venda: VGV contratado, caixa já recebido e saldo pendente dos contratos daquele mês.
                </p>
            </div>

            <div className="p-5 space-y-4">
                <CenarioResumo cards={cards} meses={meses} />

                <div className="flex items-center gap-4 text-[11px] text-gray-500">
                    <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-emerald-500" /> Recebido</span>
                    <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-amber-400" /> A receber</span>
                </div>

                {!temVendas ? (
                    <p className="text-sm text-gray-400 text-center py-6">Nenhuma venda no período selecionado.</p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full text-sm">
                            <thead>
                                <tr className="text-left text-xs uppercase tracking-wide text-gray-400 border-b border-gray-100">
                                    <th className="py-2 pr-4 font-medium">Mês de venda</th>
                                    <th className="py-2 pr-4 font-medium text-right">Contratos</th>
                                    <th className="py-2 pr-4 font-medium text-right">VGV</th>
                                    <th className="py-2 pr-4 font-medium text-right">Recebido</th>
                                    <th className="py-2 pr-4 font-medium text-right">A Receber</th>
                                    <th className="py-2 pr-4 font-medium text-right">% Recebido</th>
                                    <th className="py-2 pr-4 font-medium min-w-[100px]">Conversão</th>
                                </tr>
                            </thead>
                            <tbody>
                                {meses.map((m) => (
                                    <tr
                                        key={m.mes_ano}
                                        className={`border-b border-gray-50 ${m.quantidade === 0 ? 'text-gray-300' : 'hover:bg-gray-50'}`}
                                    >
                                        <td className="py-2.5 pr-4 font-medium text-gray-700">{m.label ?? m.mes_ano}</td>
                                        <td className="py-2.5 pr-4 text-right">{formatNumber(m.quantidade)}</td>
                                        <td className="py-2.5 pr-4 text-right text-gray-700">{formatCurrency(m.vgv)}</td>
                                        <td className="py-2.5 pr-4 text-right text-emerald-600">{formatCurrency(m.recebido)}</td>
                                        <td className="py-2.5 pr-4 text-right text-amber-600">{formatCurrency(m.a_receber)}</td>
                                        <td className="py-2.5 pr-4 text-right text-gray-700">{formatPercent(m.pct_recebido)}</td>
                                        <td className="py-2.5 pr-4">
                                            <BarraConversao vgv={m.vgv} recebido={m.recebido} aReceber={m.a_receber} />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                            {meses.length > 1 && (
                                <tfoot>
                                    <tr className="text-xs font-semibold text-gray-700 border-t border-gray-200 bg-gray-50">
                                        <td className="py-2.5 pr-4">Total período</td>
                                        <td className="py-2.5 pr-4 text-right">{formatNumber(cards.num_vendas)}</td>
                                        <td className="py-2.5 pr-4 text-right">{formatCurrency(cards.vgv)}</td>
                                        <td className="py-2.5 pr-4 text-right text-emerald-700">{formatCurrency(cards.total_recebido)}</td>
                                        <td className="py-2.5 pr-4 text-right text-amber-700">{formatCurrency(cards.total_a_receber)}</td>
                                        <td className="py-2.5 pr-4 text-right">{formatPercent(cards.pct_recebido)}</td>
                                        <td className="py-2.5 pr-4">
                                            <BarraConversao vgv={cards.vgv} recebido={cards.total_recebido} aReceber={cards.total_a_receber} />
                                        </td>
                                    </tr>
                                </tfoot>
                            )}
                        </table>
                    </div>
                )}
            </div>
        </div>
    )
}

export default VendasMensalPanel
